import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { mvpTableService } from '../../services/mvpTableService';

const AGE_GROUPS = [
  "Kinder",
  "Jugend",
  "Erwachsene",
  "Senioren",
  "Senioren Plus"
];

function convertMVPData(apiData) {
  const allAthletes = [];
  Object.keys(apiData).forEach(ageGroup => {
    const groupAthletes = apiData[ageGroup];
    groupAthletes.forEach(athlete => {
      allAthletes.push({ ...athlete, ageGroup });
    });
  });
  return allAthletes;
}

const MVPTableTailwind = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: "totalPoints", direction: "desc" });
  const [athletes, setAthletes] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMVPData();
  }, []);

  const loadMVPData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await mvpTableService.getMVPTable();
      const mvpData = response?.data || response;
      if (mvpData && typeof mvpData === 'object' && Object.keys(mvpData).length > 0) {
        const convertedData = convertMVPData(mvpData);
        setAthletes(convertedData && convertedData.length > 0 ? convertedData : []);
      } else {
        setAthletes([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der MVP-Tabellen:', error);
      setAthletes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key ? (prev.direction === "asc" ? "desc" : "asc") : "desc"
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="fas fa-sort text-slate-300 dark:text-slate-600 ml-1 text-[10px]" />;
    return sortConfig.direction === "asc"
      ? <i className="fas fa-sort-up text-primary dark:text-primary-light ml-1 text-[10px] relative top-[1px]" />
      : <i className="fas fa-sort-down text-primary dark:text-primary-light ml-1 text-[10px] relative -top-[1px]" />;
  };

  const toggleGroupExpansion = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedAgeGroup("all");
  };

  const getActiveFilters = () => {
    const active = [];
    if (selectedAgeGroup !== "all") {
      active.push({ type: "Altersgruppe", value: selectedAgeGroup, onRemove: () => setSelectedAgeGroup("all") });
    }
    if (searchTerm.trim() !== "") {
      active.push({ type: "Suche", value: searchTerm, onRemove: () => setSearchTerm("") });
    }
    return active;
  };

  const filteredAthletes = athletes.filter((athlete) => {
    const matchesSearch =
      athlete.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      athlete.nation.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAgeGroup = selectedAgeGroup === "all" || athlete.ageGroup === selectedAgeGroup;
    return matchesSearch && matchesAgeGroup;
  });

  const athletesByAgeGroup = AGE_GROUPS.reduce((acc, group) => {
    acc[group] = filteredAthletes.filter(a => a.ageGroup === group);
    return acc;
  }, {});

  Object.keys(athletesByAgeGroup).forEach(group => {
    athletesByAgeGroup[group].sort((a, b) => {
      let cmp = 0;
      if (sortConfig.key === "totalPoints") cmp = b.totalPoints - a.totalPoints;
      else if (sortConfig.key === "name") cmp = a.name.localeCompare(b.name);
      else if (sortConfig.key === "nation") cmp = a.nation.localeCompare(b.nation);
      else if (sortConfig.key === "events") cmp = b.events - a.events;
      return sortConfig.direction === "asc" ? -cmp : cmp;
    });
  });

  const activeFilters = getActiveFilters();
  const hasActiveFilters = activeFilters.length > 0;
  const visibleGroups = AGE_GROUPS.filter(
    g => selectedAgeGroup === "all" || selectedAgeGroup === g
  );

  const ageGroupOptions = [
    { value: "all", label: "Alle" },
    ...AGE_GROUPS.map(g => ({ value: g, label: g }))
  ];

  // ─── Skeleton ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-56"></div>
              <div className="h-6 w-10 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
            </div>
            <div className="h-4 w-96 bg-slate-200 dark:bg-slate-700 rounded"></div>
          </div>

          {/* Filter Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 mb-6 animate-pulse">
            <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded-xl mb-5"></div>
            <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded mb-3"></div>
            <div className="flex flex-wrap gap-1.5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-7 w-24 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
              ))}
            </div>
          </div>

          {/* Group Tables Skeleton */}
          <div className="space-y-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
                <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center gap-3">
                  <div className="h-5 w-28 bg-slate-200 dark:bg-slate-700 rounded"></div>
                  <div className="h-5 w-16 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                </div>
                <div className="divide-y divide-slate-100 dark:divide-slate-700/60">
                  {[...Array(3)].map((_, j) => (
                    <div key={j} className="flex items-center gap-4 px-6 py-3.5">
                      <div className="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded-full flex-shrink-0"></div>
                      <div className="flex-1 h-4 bg-slate-200 dark:bg-slate-700 rounded"></div>
                      <div className="w-24 h-4 bg-slate-200 dark:bg-slate-700 rounded"></div>
                      <div className="w-16 h-6 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                      <div className="w-12 h-6 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  // ─── Athlete Row Renderer ─────────────────────────────────────────────────
  const renderAthletesForGroup = (group) => {
    const groupAthletes = athletesByAgeGroup[group];
    const isExpanded = expandedGroups[group];
    const displayAthletes = isExpanded ? groupAthletes : groupAthletes.slice(0, 3);
    const hasMore = groupAthletes.length > 3;

    if (groupAthletes.length === 0) {
      return (
        <tr>
          <td colSpan={5} className="px-6 py-10 text-center">
            <div className="flex flex-col items-center gap-2 text-slate-400 dark:text-slate-500">
              <i className="fas fa-user-slash text-xl"></i>
              <p className="text-sm">Keine Athleten gefunden</p>
            </div>
          </td>
        </tr>
      );
    }

    return (
      <>
        {displayAthletes.map((athlete, idx) => (
          <tr key={athlete.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
            {/* Rang */}
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold border ${
                idx === 0
                  ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700/50'
                  : idx === 1
                    ? 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600'
                    : idx === 2
                      ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700/50'
                      : 'bg-slate-50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-600'
              }`}>
                {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `${idx + 1}`}
              </span>
            </td>

            {/* Name */}
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100">
              {athlete.name}
            </td>

            {/* Nation */}
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="flex items-center gap-2">
                <img
                  src={process.env.PUBLIC_URL + `/images/flags/${athlete.nationCode}.png`}
                  alt={athlete.nation}
                  className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                />
                <span className="text-sm text-slate-900 dark:text-slate-100">{athlete.nation}</span>
              </div>
            </td>

            {/* MVP-Punkte */}
            <td className="px-6 py-4 whitespace-nowrap text-right">
              <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-lg bg-primary/8 dark:bg-primary-light/10 text-primary dark:text-primary-light text-sm font-semibold border border-primary/15 dark:border-primary-light/15">
                {athlete.totalPoints}
              </span>
            </td>

            {/* Events */}
            <td className="px-6 py-4 whitespace-nowrap text-right">
              <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-lg bg-slate-100 dark:bg-slate-700/60 text-slate-700 dark:text-slate-300 text-sm font-semibold">
                {athlete.events}
              </span>
            </td>
          </tr>
        ))}

        {hasMore && (
          <tr className="bg-slate-50/50 dark:bg-slate-700/20">
            <td colSpan={5} className="px-6 py-3 text-center">
              <button
                onClick={() => toggleGroupExpansion(group)}
                className="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-[10px]`}></i>
                {isExpanded
                  ? t("admin.mvpTable.showLess", "Weniger anzeigen")
                  : t("admin.mvpTable.showMore", `Weitere ${groupAthletes.length - 3} Athleten anzeigen`)
                }
              </button>
            </td>
          </tr>
        )}
      </>
    );
  };

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("admin.mvpTable.title", "MVPs je Altersgruppe")}
                </h1>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                  <i className="fas fa-trophy text-[10px]"></i>
                  {filteredAthletes.length}
                </span>
              </div>
              <p className="text-slate-500 dark:text-slate-400 mt-1.5 text-sm">
                {t("admin.mvpTable.subtitle", "Die besten Athleten jeder Altersgruppe basierend auf der MVP-Punktzahl.")}
              </p>
            </div>
          </div>
        </div>

        {/* ── Filter Section ─────────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 mb-6">
          {/* Search */}
          <div className="relative mb-5">
            <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-xs pointer-events-none"></i>
            <input
              type="text"
              placeholder={t("admin.mvpTable.search", "Athlet oder Nation suchen...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 h-10 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700/50 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors"
            />
          </div>

          {/* Age Group Pills */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">
              Altersgruppe
            </p>
            <div className="flex flex-wrap gap-1.5">
              {ageGroupOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedAgeGroup(option.value)}
                  className={`inline-flex items-center h-7 px-3 text-xs font-medium rounded-full transition-colors ${
                    selectedAgeGroup === option.value
                      ? "bg-primary dark:bg-primary-light text-white shadow-sm"
                      : "bg-slate-100 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Active Filters */}
          {hasActiveFilters && (
            <div className="border-t border-slate-100 dark:border-slate-700/60 pt-4 mt-3">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  Aktive Filter
                </span>
                <button
                  onClick={handleResetFilters}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-xs font-medium transition-colors border bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-600 hover:text-primary dark:hover:text-primary-light"
                >
                  <i className="fas fa-undo text-[10px]"></i>
                  Zurücksetzen
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {activeFilters.map((filter, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/8 dark:bg-primary-light/10 text-primary dark:text-primary-light border border-primary/20 dark:border-primary-light/20"
                  >
                    {filter.type}: {filter.value}
                    <button onClick={filter.onRemove} className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity">
                      <i className="fas fa-times text-[9px]"></i>
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Age Group Tables ────────────────────────────────────────────────── */}
        {athletes.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm text-center py-16 px-8">
            <div className="w-14 h-14 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-trophy text-slate-400 dark:text-slate-500 text-xl"></i>
            </div>
            <p className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Noch keine MVP-Daten verfügbar
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Die Ergebnisse werden hier angezeigt, sobald Wettkämpfe abgeschlossen sind.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {visibleGroups.map((group) => (
              <div
                key={group}
                className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden"
              >
                {/* Group Header */}
                <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700/60 bg-slate-50/50 dark:bg-slate-700/20 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                      {group}
                    </h2>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-600">
                      {athletesByAgeGroup[group].length}
                    </span>
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                    <thead className="bg-slate-50 dark:bg-slate-700/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-14">
                          #
                        </th>
                        <th
                          onClick={() => handleSort("name")}
                          className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none transition-colors ${
                            sortConfig.key === "name"
                              ? "text-primary dark:text-primary-light bg-primary/5 dark:bg-primary-light/5"
                              : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                          }`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {t("admin.mvpTable.name", "Name")}
                            {getSortIcon("name")}
                          </span>
                        </th>
                        <th
                          onClick={() => handleSort("nation")}
                          className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none transition-colors ${
                            sortConfig.key === "nation"
                              ? "text-primary dark:text-primary-light bg-primary/5 dark:bg-primary-light/5"
                              : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                          }`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {t("admin.mvpTable.nation", "Nation")}
                            {getSortIcon("nation")}
                          </span>
                        </th>
                        <th
                          onClick={() => handleSort("totalPoints")}
                          className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider cursor-pointer select-none transition-colors ${
                            sortConfig.key === "totalPoints"
                              ? "text-primary dark:text-primary-light bg-primary/5 dark:bg-primary-light/5"
                              : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                          }`}
                        >
                          <span className="inline-flex items-center justify-end gap-1 w-full">
                            {t("admin.mvpTable.totalPoints", "MVP-Punkte")}
                            {getSortIcon("totalPoints")}
                          </span>
                        </th>
                        <th
                          onClick={() => handleSort("events")}
                          className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider cursor-pointer select-none transition-colors ${
                            sortConfig.key === "events"
                              ? "text-primary dark:text-primary-light bg-primary/5 dark:bg-primary-light/5"
                              : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                          }`}
                        >
                          <span className="inline-flex items-center justify-end gap-1 w-full">
                            {t("admin.mvpTable.events", "Events")}
                            {getSortIcon("events")}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                      {renderAthletesForGroup(group)}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default MVPTableTailwind;
